import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import {
  CheckCircle2, ExternalLink, EyeOff, Info, Link2, Loader2, MapPin, MessageSquareQuote, Plus, Search, Star, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  AdminPage, EmptyState, FilterChip, PageHeader, Panel, PersonAvatar, StatCard, StatusChip, Toolbar, ToolbarSpacer,
  adminButton, adminInput, useAdminPageTitle,
} from '@/components/admin/ui';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useToast } from '@/hooks/use-toast';
import {
  useAddReview, useAdminReviews, useDeleteReview, useSetReviewApproval, type AdminReview,
} from '@/hooks/useAdminReviews';
import {
  GOOGLE_MAX_REVIEWS, useConnectGooglePlace, useDisconnectGoogle, useGoogleReviewsPreview, useGoogleReviewsSettings,
  useSearchGooglePlaces, useSetGoogleReviewsEnabled, type GoogleLiveReview, type GooglePlaceSearchResult,
} from '@/hooks/useGoogleReviews';
import { GoogleGlyph } from '@/components/reviews/GoogleGlyph';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function Stars({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) {
  const cls = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`} role="img">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          strokeWidth={0}
          className={cn(cls, i <= Math.round(rating) ? 'fill-amber-400' : 'fill-muted-foreground/25')}
        />
      ))}
    </span>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  public_form: 'Website form',
  admin_entry: 'Added by you',
  import: 'Imported',
};

function SourceBadge({ source }: { source: string }) {
  return <span className="adm-chip adm-chip--grey">{SOURCE_LABEL[source] ?? source.replace('_', ' ')}</span>;
}

const safeDate = (iso: string | null | undefined, fmt: (d: Date) => string) => {
  if (!iso) return '';
  try {
    return fmt(parseISO(iso));
  } catch {
    return '';
  }
};

// ---------------------------------------------------------------------------
// Add testimonial (owner-entered, approved immediately)
// ---------------------------------------------------------------------------

const addReviewSchema = z.object({
  customer_name: z.string().trim().min(2, 'Add the customer’s name').max(120),
  customer_location: z.string().trim().max(120).optional().default(''),
  content: z.string().trim().min(10, 'Reviews need at least 10 characters').max(1000, 'Keep it under 1000 characters'),
  rating: z.number().int().min(1).max(5),
});
type AddReviewValues = z.infer<typeof addReviewSchema>;

function AddReviewDialog({ open, onOpenChange, orgId }: { open: boolean; onOpenChange: (open: boolean) => void; orgId: string | null }) {
  const { toast } = useToast();
  const { assertCanWrite } = useReadOnlyGuard();
  const addReview = useAddReview(orgId);
  const form = useForm<AddReviewValues>({
    resolver: zodResolver(addReviewSchema),
    defaultValues: { customer_name: '', customer_location: '', content: '', rating: 5 },
  });

  const onSubmit = (values: AddReviewValues) => {
    if (!assertCanWrite()) return;
    addReview.mutate(
      { customer_name: values.customer_name, customer_location: values.customer_location ?? '', content: values.content, rating: values.rating },
      {
        onSuccess: () => {
          toast({ title: 'Review added', description: 'It’s published on your website.' });
          form.reset();
          onOpenChange(false);
        },
        onError: () => toast({ title: 'Unable to add review', variant: 'destructive' }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Add testimonial</DialogTitle>
          <DialogDescription>Owner-entered testimonials are approved immediately.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="adm-form space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="customer_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer name</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="customer_location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location <span className="font-normal text-muted-foreground">(optional)</span></FormLabel>
                    <FormControl><Input placeholder="e.g. Clifton" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Review</FormLabel>
                  <FormControl><Textarea rows={4} {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="rating"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rating</FormLabel>
                  <div className="flex items-center gap-1" role="radiogroup" aria-label="Rating">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={field.value === value}
                        aria-label={`${value} stars`}
                        onClick={() => field.onChange(value)}
                        className="rounded-md p-1 hover:bg-muted"
                      >
                        <Star strokeWidth={0} className={cn('h-5 w-5', value <= field.value ? 'fill-amber-400' : 'fill-muted-foreground/25')} />
                      </button>
                    ))}
                  </div>
                </FormItem>
              )}
            />
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" size="sm" variant="ghost" className={adminButton.ghost} onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" size="sm" className={adminButton.primary} disabled={addReview.isPending}>
                {addReview.isPending ? <Loader2 className="animate-spin" /> : <Plus />} Add review
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Google reviews (live — nothing but the place id is stored)
// ---------------------------------------------------------------------------

const searchSchema = z.object({
  query: z.string().trim().min(2, 'Enter your business name').max(120),
  town: z.string().trim().max(80).optional().default(''),
});
type SearchValues = z.infer<typeof searchSchema>;

function GoogleReviewPreview({ review }: { review: GoogleLiveReview }) {
  return (
    <li className="rounded-lg border border-border bg-card p-3" data-testid="google-preview-review">
      <div className="flex items-center gap-2.5">
        <PersonAvatar name={review.authorName} src={review.authorPhotoUrl} size={28} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-semibold text-foreground">
            {review.authorUrl ? (
              <a href={review.authorUrl} target="_blank" rel="noopener noreferrer nofollow" className="hover:underline">{review.authorName}</a>
            ) : review.authorName}
          </p>
          <p className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <Stars rating={review.rating} />
            {review.relativeTime && <span>{review.relativeTime}</span>}
          </p>
        </div>
      </div>
      <p className="mt-2 line-clamp-3 text-[12.5px] leading-relaxed text-foreground/85">{review.text}</p>
    </li>
  );
}

function GoogleReviewsPanel({ orgId, canWrite }: { orgId: string | null; canWrite: boolean }) {
  const { toast } = useToast();
  const { assertCanWrite } = useReadOnlyGuard();
  const settings = useGoogleReviewsSettings(orgId);
  const placeId = settings.data?.placeId ?? null;
  const preview = useGoogleReviewsPreview(orgId, placeId);
  const search = useSearchGooglePlaces(orgId);
  const connect = useConnectGooglePlace(orgId);
  const setEnabled = useSetGoogleReviewsEnabled(orgId);
  const disconnect = useDisconnectGoogle(orgId);

  const [changing, setChanging] = useState(false);
  const [selected, setSelected] = useState<GooglePlaceSearchResult | null>(null);
  const form = useForm<SearchValues>({ resolver: zodResolver(searchSchema), defaultValues: { query: '', town: '' } });

  const connected = Boolean(placeId);
  const showSearch = !connected || changing;
  const live = preview.data ?? null;

  const resetSearch = () => {
    setChanging(false);
    setSelected(null);
    search.reset();
  };

  const onSearch = (values: SearchValues) => {
    if (!assertCanWrite()) return;
    setSelected(null);
    search.mutate(
      { query: values.query, town: values.town },
      { onError: (error) => toast({ title: 'Google search failed', description: error.message, variant: 'destructive' }) },
    );
  };

  const onConnect = () => {
    if (!selected || !assertCanWrite()) return;
    connect.mutate(
      { placeId: selected.placeId },
      {
        onSuccess: () => {
          resetSearch();
          toast({ title: 'Google business connected', description: 'Your Google reviews now show on your website.' });
        },
        onError: (error) => toast({ title: 'Unable to connect', description: error.message, variant: 'destructive' }),
      },
    );
  };

  const onToggle = (enabled: boolean) => {
    if (!assertCanWrite()) return;
    setEnabled.mutate(enabled, {
      onSuccess: () => toast({ title: enabled ? 'Google reviews shown on your website' : 'Google reviews hidden from your website' }),
      onError: (error) => toast({ title: 'Unable to update', description: error.message, variant: 'destructive' }),
    });
  };

  const onDisconnect = () => {
    if (!assertCanWrite()) return;
    disconnect.mutate(undefined, {
      onSuccess: () => {
        resetSearch();
        toast({ title: 'Google business disconnected' });
      },
      onError: (error) => toast({ title: 'Unable to disconnect', description: error.message, variant: 'destructive' }),
    });
  };

  const openOnGoogle = live?.place.googleMapsUri ?? live?.place.reviewsUrl ?? null;

  return (
    <Panel
      title="Google reviews"
      icon={<GoogleGlyph className="h-4 w-4" />}
      description="Show your latest Google reviews on your website, live from Google, with a link to read the rest."
      data-testid="google-reviews-panel"
    >
      <div className="space-y-4">
        {settings.isLoading ? (
          <div className="h-24 animate-pulse rounded-lg bg-muted" />
        ) : connected && !changing ? (
          <>
            <div className="rounded-xl border border-border p-3.5" data-testid="google-connected-card">
              {preview.isLoading ? (
                <div className="space-y-2" aria-label="Loading Google reviews">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
                </div>
              ) : preview.isError || !live ? (
                <p className="text-[12.5px] text-destructive">Couldn’t load your Google reviews right now. {preview.error instanceof Error ? preview.error.message : ''}</p>
              ) : (
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-card">
                    <GoogleGlyph className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13.5px] font-semibold text-foreground">{live.place.name}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
                      {live.place.rating != null && (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="adm-num font-semibold text-foreground">{live.place.rating.toFixed(1)}</span>
                          <Stars rating={live.place.rating} />
                        </span>
                      )}
                      <span>{live.place.reviewCount.toLocaleString()} reviews on Google</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-3 text-[12px]">
                {openOnGoogle && (
                  <a href={openOnGoogle} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                    Open on Google <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                <span className="flex-1" />
                <button type="button" className="font-medium text-muted-foreground hover:text-foreground disabled:opacity-50" disabled={!canWrite} onClick={() => setChanging(true)}>
                  Change
                </button>
                <button type="button" className="font-medium text-muted-foreground hover:text-destructive disabled:opacity-50" disabled={!canWrite || disconnect.isPending} onClick={onDisconnect}>
                  Disconnect
                </button>
              </div>
            </div>

            <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
              <div className="min-w-0">
                <Label htmlFor="google-reviews-enabled" className="text-[13px] font-semibold text-foreground">Show Google reviews on my website</Label>
                <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
                  {settings.data?.enabled ? 'Showing in the Reviews section of your website.' : 'Hidden — only you can see this preview.'}
                </p>
              </div>
              <Switch
                id="google-reviews-enabled"
                checked={settings.data?.enabled === true}
                onCheckedChange={onToggle}
                disabled={!canWrite || setEnabled.isPending}
              />
            </div>

            {live && live.reviews.length > 0 && (
              <div className="space-y-2">
                <p className="adm-label">Preview · as shown on your website</p>
                <ul className="space-y-2">
                  {live.reviews.map((review) => <GoogleReviewPreview key={review.id} review={review} />)}
                </ul>
              </div>
            )}
          </>
        ) : null}

        {showSearch && !settings.isLoading && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSearch)} className="adm-form space-y-3" aria-label="Find your business on Google">
              <div className="grid gap-3 sm:grid-cols-[1fr_0.8fr] lg:grid-cols-1 xl:grid-cols-[1fr_0.8fr]">
                <FormField
                  control={form.control}
                  name="query"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business name</FormLabel>
                      <FormControl><Input placeholder="e.g. Sparkle & Co" disabled={!canWrite} {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="town"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Town or city</FormLabel>
                      <FormControl><Input placeholder="e.g. Bristol" disabled={!canWrite} {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" size="sm" className={adminButton.outline} disabled={!canWrite || search.isPending}>
                  {search.isPending ? <Loader2 className="animate-spin" /> : <Search />} Find on Google
                </Button>
                {connected && (
                  <Button type="button" size="sm" variant="ghost" className={adminButton.ghost} onClick={resetSearch}>Cancel</Button>
                )}
              </div>
            </form>
          </Form>
        )}

        {showSearch && search.data && (
          <div className="space-y-2" data-testid="google-search-results">
            <p className="adm-label">{search.data.length ? 'Pick your business' : 'No matches'}</p>
            {search.data.length === 0 && (
              <p className="text-[12.5px] text-muted-foreground">Try the exact name shown on Google Maps, or add your town.</p>
            )}
            <ul className="space-y-1.5" role="listbox" aria-label="Google search results">
              {search.data.map((place) => {
                const isSelected = selected?.placeId === place.placeId;
                return (
                  <li key={place.placeId}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => setSelected(place)}
                      className={cn(
                        'flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors',
                        isSelected ? 'border-primary bg-secondary' : 'border-border hover:bg-muted',
                      )}
                    >
                      <MapPin className={cn('mt-0.5 h-4 w-4 shrink-0', isSelected ? 'text-primary' : 'text-muted-foreground')} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-foreground">{place.name}</span>
                        <span className="block truncate text-[12px] text-muted-foreground">{place.address}</span>
                      </span>
                      <span className="shrink-0 text-right text-[12px] text-muted-foreground">
                        {place.rating != null ? (
                          <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                            <Star strokeWidth={0} className="h-3.5 w-3.5 fill-amber-400" />
                            {place.rating.toFixed(1)}
                          </span>
                        ) : null}
                        <span className="block">{place.reviewCount.toLocaleString()} reviews</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {selected && (
              <Button size="sm" className={adminButton.primary} disabled={!canWrite || connect.isPending} onClick={onConnect}>
                {connect.isPending ? <Loader2 className="animate-spin" /> : <Link2 />} Connect this business
              </Button>
            )}
          </div>
        )}

        <p className="flex gap-2 text-[12px] leading-relaxed text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Google shares up to {GOOGLE_MAX_REVIEWS} reviews; customers can read the rest on Google. Reviews are shown live from Google and aren’t
            copied into your review list.
          </span>
        </p>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Review list
// ---------------------------------------------------------------------------

type Filter = 'all' | 'pending' | 'published';

function ReviewRow({
  review, canWrite, onToggle, onDelete, busy,
}: {
  review: AdminReview;
  canWrite: boolean;
  onToggle: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const date = safeDate(review.created_at, (d) => format(d, 'd MMM yyyy'));
  return (
    <li className="flex gap-3 px-4 py-4 md:px-5" data-testid="admin-review-row">
      <PersonAvatar name={review.customer_name} size={36} />
      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-x-3 sm:gap-y-0">
          <div className="min-w-0 sm:col-start-1 sm:row-start-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="mr-1 truncate text-[13.5px] font-semibold text-foreground">{review.customer_name}</p>
              <StatusChip tone={review.is_approved ? 'green' : 'amber'}>{review.is_approved ? 'Published' : 'Pending'}</StatusChip>
              <SourceBadge source={review.source} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
              <Stars rating={review.rating} />
              {date && <span>{date}</span>}
              {review.customer_location && <span>· {review.customer_location}</span>}
            </div>
          </div>
          <div className="order-last flex shrink-0 items-center gap-1.5 sm:order-none sm:col-start-2 sm:row-start-1 sm:self-start">
            <Button size="sm" variant="outline" className={adminButton.outline} disabled={!canWrite || busy} onClick={onToggle}>
              {review.is_approved ? <EyeOff /> : <CheckCircle2 />}
              {review.is_approved ? 'Unpublish' : 'Approve'}
            </Button>
            <Button size="sm" variant="ghost" className={cn(adminButton.iconGhost, 'hover:text-destructive')} disabled={!canWrite || busy} onClick={onDelete} aria-label={`Delete review from ${review.customer_name}`}>
              <Trash2 />
            </Button>
          </div>
        <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-foreground/85 line-clamp-5 sm:col-span-2 sm:row-start-2 sm:mt-2">{review.content}</p>
      </div>
    </li>
  );
}

const ReviewsAdmin = () => {
  useAdminPageTitle('Reviews');
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const { toast } = useToast();

  const { data: reviews = [], isLoading, error } = useAdminReviews(orgId);
  const setApproval = useSetReviewApproval(orgId);
  const deleteReview = useDeleteReview(orgId);

  const [filter, setFilter] = useState<Filter>('all');
  const [term, setTerm] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AdminReview | null>(null);
  const googleSettings = useGoogleReviewsSettings(orgId);
  const googleLive = useGoogleReviewsPreview(orgId, googleSettings.data?.placeId).data ?? null;

  const stats = useMemo(() => {
    const published = reviews.filter((r) => r.is_approved);
    const average = published.length ? published.reduce((sum, r) => sum + r.rating, 0) / published.length : null;
    return {
      total: reviews.length,
      published: published.length,
      pending: reviews.length - published.length,
      average,
    };
  }, [reviews]);

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase();
    return reviews.filter((r) => {
      if (filter === 'pending' && r.is_approved) return false;
      if (filter === 'published' && !r.is_approved) return false;
      if (!q) return true;
      return r.customer_name.toLowerCase().includes(q) || r.content.toLowerCase().includes(q);
    });
  }, [reviews, filter, term]);

  const toggle = (review: AdminReview) => {
    if (!assertCanWrite()) return;
    setApproval.mutate(
      { id: review.id, approved: !review.is_approved },
      {
        onSuccess: () => toast({ title: review.is_approved ? 'Review unpublished' : 'Review published' }),
        onError: () => toast({ title: 'Unable to update review', variant: 'destructive' }),
      },
    );
  };

  const confirmDelete = () => {
    if (!pendingDelete || !assertCanWrite()) return;
    deleteReview.mutate(pendingDelete.id, {
      onSuccess: () => toast({ title: 'Review deleted' }),
      onError: () => toast({ title: 'Unable to delete review', variant: 'destructive' }),
      onSettled: () => setPendingDelete(null),
    });
  };

  return (
    <AdminPage>
      <PageHeader
        description="Capture testimonials, connect your Google reviews and choose what appears on your website. Public submissions stay pending until approved."
        actions={
          <Button size="sm" className={adminButton.primary} disabled={!canWrite} onClick={() => setAddOpen(true)}>
            <Plus /> Add review
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Average rating" icon={<Star />} value={stats.average != null ? stats.average.toFixed(1) : '—'} hint={stats.average != null ? 'Published reviews' : 'Nothing published yet'} />
        <StatCard label="Published" icon={<CheckCircle2 />} value={stats.published} hint="Live on your website" />
        <StatCard label="Pending approval" icon={<MessageSquareQuote />} value={stats.pending} hint={stats.pending ? 'Waiting for you' : 'All caught up'} />
        <StatCard
          label="Google rating"
          icon={<GoogleGlyph />}
          value={googleLive?.place.rating != null ? googleLive.place.rating.toFixed(1) : '—'}
          hint={googleLive ? `${googleLive.place.reviewCount.toLocaleString()} reviews on Google` : googleSettings.data?.placeId ? 'Loading…' : 'Not connected'}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-12">
        <Panel
          className="order-2 lg:order-none lg:col-span-8"
          title="All reviews"
          description="Approve a review to show it in the Reviews section of your website."
          padded={false}
        >
          <Toolbar className="px-4 pb-3 pt-3 md:px-5">
            <FilterChip active={filter === 'all'} count={stats.total} onClick={() => setFilter('all')}>All</FilterChip>
            <FilterChip active={filter === 'pending'} count={stats.pending} onClick={() => setFilter('pending')}>Pending</FilterChip>
            <FilterChip active={filter === 'published'} count={stats.published} onClick={() => setFilter('published')}>Published</FilterChip>
            <ToolbarSpacer />
            <div className="relative w-full sm:w-56">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search reviews" aria-label="Search reviews" className={cn(adminInput, 'pl-8')} />
            </div>
          </Toolbar>
          <div className="border-t border-border">
            {isLoading ? (
              <div className="space-y-3 p-5">
                {[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />)}
              </div>
            ) : error ? (
              <p className="p-5 text-sm text-destructive">Unable to load reviews.</p>
            ) : visible.length === 0 ? (
              <EmptyState
                icon={<MessageSquareQuote />}
                title={reviews.length === 0 ? 'No reviews yet' : 'No reviews match'}
                description={
                  reviews.length === 0
                    ? 'Add a testimonial, or add a “Leave a review” block to your website so customers can send one.'
                    : 'Try a different filter or search.'
                }
              />
            ) : (
              <ul className="divide-y divide-border" aria-label="Reviews">
                {visible.map((review) => (
                  <ReviewRow
                    key={review.id}
                    review={review}
                    canWrite={canWrite}
                    busy={(setApproval.isPending && setApproval.variables?.id === review.id) || (deleteReview.isPending && deleteReview.variables === review.id)}
                    onToggle={() => toggle(review)}
                    onDelete={() => setPendingDelete(review)}
                  />
                ))}
              </ul>
            )}
          </div>
        </Panel>

        <div className="order-1 space-y-5 lg:order-none lg:col-span-4">
          <GoogleReviewsPanel orgId={orgId} canWrite={canWrite} />
        </div>
      </div>

      <AddReviewDialog open={addOpen} onOpenChange={setAddOpen} orgId={orgId} />

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this review?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the review. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPage>
  );
};

export default ReviewsAdmin;
